import React, { useCallback, useMemo, useState } from "react"
import { BottomSheet } from "react-spring-bottom-sheet"
import "react-spring-bottom-sheet/dist/style.css"

import { showInfoToast } from "@fuse/sharedComponents/themeToast/toastUtils"

import {
  TDeviceType,
  useDeviceTypeByWidth,
} from "@fuse/customHooks/useDeviceTypeByWidth"

import IconChat from "@fuse/assets/icons/img-icon-chat2.png"
import IconGmail from "@fuse/assets/images/socialMedia/img-gmail.png"
import IconSms from "@fuse/assets/images/socialMedia/img-sms.png"
import IconWhatsapp from "@fuse/assets/images/socialMedia/img-whatsapp.png"

interface SupportChatModalProps {
  isOpen: boolean
  onClose: () => void
  fromScreen?: string
  userId?: string
  showSmsOnMobile?: boolean
  smsPhoneNumber?: string
}

interface SupportChatTriggerProps {
  onClick: () => void
  className?: string
  iconSize?: string
  textSize?: string
}

export const SupportChatTrigger: React.FC<SupportChatTriggerProps> = ({
  onClick,
  className = "text-left text-sm sm:text-xs font-medium bg-transparent flex gap-2 justify-start items-center p-0 w-full",
  iconSize = "h-[18px] w-[18px]",
  textSize = "text-white/80",
}) => {
  return (
    <button onClick={onClick} className={className}>
      <img
        src={IconChat}
        alt="Chat icon"
        className={iconSize}
        style={{
          filter:
            "brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(60%)",
        }}
      />
      <div className={textSize}>
        HAVING ISSUES? WE CAN HELP.{" "}
        <span className="underline">TALK TO OUR TEAM.</span>
      </div>
    </button>
  )
}

const SupportChatModal: React.FC<SupportChatModalProps> = ({
  isOpen,
  onClose,
  fromScreen = "unknown",
  userId,
  showSmsOnMobile = true,
  smsPhoneNumber = "+18333226009",
}) => {
  const deviceType = useDeviceTypeByWidth()
  const isMobile = deviceType === TDeviceType.MOBILE

  // Support mail data
  const supportMailData = useMemo(() => {
    const recipient = "letshang-support@vyral.info"
    const subject = "Assistance needed: Issue on Letshang app"
    const body = `Hello,\n\nI'm having an issue.\n<Please explain your issue here>\n\nReport details:\nScreen: ${fromScreen}\nTime: ${new Date().toLocaleString()}\nUser ID: ${userId ?? "Not provided"}\n\nRegards`
    const url = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    return { url, recipient }
  }, [fromScreen, userId])

  // WhatsApp message data
  const whatsappMessage = useMemo(() => {
    return `Hello,\n\nI'm having an issue with Let's Hang.\n<Please explain your issue here>\n\nReport details:\nScreen: ${fromScreen}\nTime: ${new Date().toLocaleString()}\nUser ID: ${userId ?? "Not provided"}\n\nRegards`
  }, [fromScreen, userId])

  // Copy to clipboard function
  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(supportMailData.recipient)
    showInfoToast("Email address copied to clipboard!")
    onClose()
  }, [onClose, supportMailData.recipient])

  // SMS handler function
  const handleSmsClick = useCallback(() => {
    const message = `Hello,\n\nI'm having an issue with Let's Hang.\n<Please explain your issue here>\n\nReport details:\nScreen: ${fromScreen}\nTime: ${new Date().toLocaleString()}\nUser ID: ${userId ?? "Not provided"}\n\nRegards`
    const smsUrl = `sms:${smsPhoneNumber}?body=${encodeURIComponent(message)}`
    window.location.href = smsUrl
    onClose()
  }, [fromScreen, userId, smsPhoneNumber, onClose])

  const renderContactOptions = () => (
    <div className="flex flex-col gap-4 mb-8">
      <div
        onClick={() => {
          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`
          window.open(whatsappUrl, "_blank")
          onClose()
        }}
        className="flex items-center gap-4 p-4 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-200 cursor-pointer"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-500">
          <img
            src={IconWhatsapp}
            alt="WhatsApp"
            className="h-6 w-6 object-contain"
            style={{
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
            }}
          />
        </div>
        <span className="text-white font-medium text-lg">Chat on WhatsApp</span>
      </div>

      <div
        onClick={() => {
          window.location.href = supportMailData.url
          onClose()
        }}
        className="flex items-center gap-4 p-4 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-200 cursor-pointer"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500">
          <img
            src={IconGmail}
            alt="Gmail"
            className="h-6 w-6 object-contain"
            style={{
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
            }}
          />
        </div>
        <span className="text-white font-medium text-lg">Email Us</span>
      </div>

      {/* SMS option - only show on mobile if enabled */}
      {isMobile && showSmsOnMobile && (
        <div
          onClick={handleSmsClick}
          className="flex items-center gap-4 p-4 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-200 cursor-pointer"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-600">
            <img
              src={IconSms}
              alt="SMS"
              className="h-6 w-6 object-contain"
              style={{
                backgroundColor: "transparent",
                border: "none",
                boxShadow: "none",
              }}
            />
          </div>
          <span className="text-white font-medium text-lg">Send SMS</span>
        </div>
      )}
    </div>
  )

  const renderFooter = () => (
    <div className="border-t border-white/20 pt-6">
      <p className="text-white/70 text-sm mb-2 text-left">
        If the links don't work, just email us at:
      </p>
      <div className="flex items-center gap-2 justify-start">
        <span className="text-blue-400 text-sm font-medium">
          {supportMailData.recipient}
        </span>
        <button
          onClick={copyToClipboard}
          className="text-white/50 hover:text-white bg-transparent border-none p-0"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
          </svg>
        </button>
      </div>
    </div>
  )

  const renderModalContent = () => (
    <>
      {/* Header with close button */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-white font-medium text-xl">Talk to our team</h3>
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white text-2xl leading-none bg-transparent border-none"
        >
          ×
        </button>
      </div>

      {/* Subtitle */}
      <p className="text-white/70 text-sm mb-6 text-left">
        Choose how you'd like to connect with us:
      </p>

      {/* Contact options */}
      {renderContactOptions()}

      {/* Footer */}
      {renderFooter()}
    </>
  )

  if (!isOpen) return null

  return (
    <>
      {isMobile ? (
        <BottomSheet
          open={isOpen}
          onDismiss={onClose}
          className="!bg-transparent"
          style={
            {
              "--rsbs-bg": "transparent",
              "--rsbs-handle-bg": "#999999",
            } as React.CSSProperties
          }
        >
          <div className="p-6 rounded-2xl bg-white/20 backdrop-blur-md">
            {renderModalContent()}
          </div>
        </BottomSheet>
      ) : (
        <div
          className="fixed inset-0 bg-black/50 flex justify-center items-center z-50"
          onClick={onClose}
        >
          <div
            className="max-w-md w-full bg-white bg-opacity-20 backdrop-blur-md rounded-2xl p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 rounded-2xl">{renderModalContent()}</div>
          </div>
        </div>
      )}
    </>
  )
}

export default SupportChatModal
